import { FC, useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { Person, BusinessCenter, Group, Domain, Search, GetApp } from '@material-ui/icons';
import { useStyles } from './styles';
// import { CircularProgress } from '@material-ui/core';
import { useStore } from 'hooks/useRootStore';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useAppPreloader } from 'hooks/useAppPreloader';
interface TimelineProps {
  events?: any;
  loading?: boolean;
  userName?: string;
  userEmail?: string;
  header?: string;
}

const Timeline: FC<TimelineProps> = ({ events = [], loading = false, header, userName, userEmail }) => {
  const classes = useStyles();
  const {
    ldapStore: { viewTimelineUser, viewTimelineAdmin, viewTimeline },
    notificationStore: { openNotification },
  } = useStore();

  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { timelineEvents, displayName, displayEmail } = useMemo(() => {
    let timelineEvents: any[] = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    let displayName = userName;
    let displayEmail = userEmail;

    if (events && typeof events === 'object' && events.timeline && Array.isArray(events.timeline)) {
      timelineEvents = events.timeline;
      displayName = displayName || events.name || events.group_name || events.department_name;
      displayEmail = displayEmail || events.email;
    } else if (Array.isArray(events)) {
      timelineEvents = events;
    }
    return {
      timelineEvents,
      displayName,
      displayEmail,
    };
  }, [events, userName, userEmail]);

  const filteredEvents = useMemo(() => {
    if (!filterText.trim()) return timelineEvents;
    const q = filterText.toLowerCase();
    return timelineEvents.filter(
      (e: any) =>
        (e.raw_description || '').toLowerCase().includes(q) ||
        (e.type || '').toLowerCase().includes(q) ||
        (e.created_at || e.createdAt || '').toLowerCase().includes(q)
    );
  }, [timelineEvents, filterText]);

  const paginatedEvents = useMemo(
    () => filteredEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredEvents, page, rowsPerPage]
  );

  const handleExportExcel = () => {
    const rows = filteredEvents.map((e: any) => ({
      Type: e.type || '',
      Description: e.raw_description || '',
      Date: e.created_at
        ? new Date(e.created_at).toLocaleString()
        : e.createdAt
        ? new Date(e.createdAt).toLocaleString()
        : e.createtimestamp
        ? new Date(e.createtimestamp).toLocaleString()
        : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timeline');
    XLSX.writeFile(wb, `timeline_${displayName || 'export'}.xlsx`);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'user':
        return <Person />;
      case 'app':
        return <BusinessCenter />;
      case 'department':
        return <Domain />;
      case 'group':
        return <Group />;
      default:
        return <Person />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'user':
        return 'primary';
      case 'app':
        return 'secondary';
      case 'department':
        return 'default';
      case 'group':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getClickedUser = useMutation('viewUser', (ClientId: number) => viewTimelineUser(ClientId, header), {
    onSuccess: (user) => {
      let url;

      if (user?.data?.message === 'End User Group and Department') {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      }
      localStorage.setItem('ClickedUser', JSON.stringify(user.data.user));
      if (user?.data?.message === 'User found') {
        url = `${handleCompanyUrl('/user/view')}`;
        window.open(url, '_blank');
      } else if (user?.data?.message === 'Group found') {
        url = `${handleCompanyUrl('/group/users')}`;
        window.open(url, '_blank');
      } else if (user?.data?.message === 'Department found') {
        url = `${handleCompanyUrl('/department/users')}`;
        window.open(url, '_blank');
      } else {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      }
      window.open(url, '_blank');
    },
    onError: (error: any) => {
      openNotification('error', error.data?.message || error.message || 'User not found');
    },
  });

  const getClicked = useMutation('view', ({ id, type }: { id: number; type: string }) => viewTimeline(id, type), {
    onSuccess: (user) => {
      let url;
      if (user?.data?.message === 'End User Group and Department') {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      }
      localStorage.setItem('ClickedUser', JSON.stringify(user.data.user));
      if (user?.data?.message === 'User found') {
        url = `${handleCompanyUrl('/user/view')}`;
        window.open(url, '_blank');
      } else if (user?.data?.message === 'Group found') {
        url = `${handleCompanyUrl('/group/users')}`;
        window.open(url, '_blank');
      } else if (user?.data?.message === 'Department found') {
        url = `${handleCompanyUrl('/department/users')}`;
        window.open(url, '_blank');
      } else {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      }
      window.open(url, '_blank');
    },
    onError: (error: any) => {
      openNotification('error', error.data?.message || error.message || 'User not found');
    },
  });
  const getClickedAdmin = useMutation('viewUser', (ClientId: number) => viewTimelineAdmin(ClientId), {
    onSuccess: (user) => {
      let url;
      if (user?.data?.message === 'End User Group and Department') {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      } else {
        url = `${handleCompanyUrl('/admin/profile')}`;
        window.open(url, '_blank');
      }
    },
    onError: (error: any) => {
      openNotification('error', error.data?.message || error.message || 'User not found');
    },
  });

  const history = useHistory();

  const APP_ROUTES: Record<string, string> = {
    google: '/apps/google',
    zoom: '/apps/zoom',
    microsoft: '/apps/microsoft',
    github: '/apps/github',
  };

  const profileFromStorage = JSON.parse(localStorage.getItem('Profile') || 'null');
  const renderDescription = (event: any) => {
    const desc: string = event.raw_description || '';

    if (!desc) return null;

    const nodes: React.ReactNode[] = [];

    const ls: React.CSSProperties = {
      color: '#1976d2',
      cursor: 'pointer',
      textDecoration: 'underline',
    };

    // =========================================================
    // 1. Split " by <actor>" from the end
    // =========================================================

    const byIndex = desc.lastIndexOf(' by ');

    const mainPart = byIndex !== -1 ? desc.slice(0, byIndex) : desc;

    const actorPart = byIndex !== -1 ? desc.slice(byIndex + 4) : null;
    const userId = event?.user_id || event?.group_id || event?.department_id;

    const actorId = event?.actor_id || event?.created_by_id || event?.admin_id || event?.updated_by;

    const hasUserSyncFields =
      /First Name:\s*[^,\]]+/i.test(mainPart) ||
      /Last Name:\s*[^,\]]+/i.test(mainPart) ||
      /Email:\s*[^,\]]+/i.test(mainPart);

    if (hasUserSyncFields) {
      /*
       * Match only the things which should potentially
       * become blue.
       *
       * We intentionally DO NOT match every email.
       * Email inside (...) must remain normal.
       */
      const tokenRegex =
        /First Name:\s*([^,\]]+)|Last Name:\s*([^,\]]+)|Email:\s*([^,\]]+)|\b(Google|Zoom|Microsoft|Github|GitHub)\b|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/gi;

      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = tokenRegex.exec(mainPart)) !== null) {
        // -----------------------------------------------------
        // Normal text before matched token
        // -----------------------------------------------------

        if (match.index > lastIndex) {
          nodes.push(<span key={`normal-${lastIndex}`}>{mainPart.slice(lastIndex, match.index)}</span>);
        }

        // -----------------------------------------------------
        // First Name
        // -----------------------------------------------------

        if (match[1]) {
          const fullMatch = match[0];

          const value = match[1].trim();

          const valueStartInMatch = fullMatch.indexOf(match[1]);

          // "First Name: "
          nodes.push(<span key={`first-label-${match.index}`}>{fullMatch.slice(0, valueStartInMatch)}</span>);

          // "Alexande Eum" -> BLUE
          nodes.push(
            <span
              key={`first-value-${match.index}`}
              style={ls}
              onClick={() => {
                if (userId) {
                  getClickedUser.mutate(userId);
                }
              }}
            >
              {value}
            </span>
          );
        }

        // -----------------------------------------------------
        // Last Name
        // -----------------------------------------------------
        else if (match[2]) {
          const fullMatch = match[0];

          const value = match[2].trim();

          const valueStartInMatch = fullMatch.indexOf(match[2]);

          // "Last Name: "
          nodes.push(<span key={`last-label-${match.index}`}>{fullMatch.slice(0, valueStartInMatch)}</span>);

          // "User" -> BLUE
          nodes.push(
            <span
              key={`last-value-${match.index}`}
              style={ls}
              onClick={() => {
                if (userId) {
                  getClickedUser.mutate(userId);
                }
              }}
            >
              {value}
            </span>
          );
        }
        // -----------------------------------------------------
        // Email:
        // -----------------------------------------------------
        else if (match[3]) {
          const fullMatch = match[0];

          const value = match[3].trim();

          const valueStartInMatch = fullMatch.indexOf(match[3]);

          // "Email: " -> NORMAL
          nodes.push(<span key={`email-label-${match.index}`}>{fullMatch.slice(0, valueStartInMatch)}</span>);

          // "abc@example.com" -> BLUE
          nodes.push(
            <span
              key={`email-value-${match.index}`}
              style={ls}
              onClick={() => {
                if (userId) {
                  getClickedUser.mutate(userId);
                }
              }}
            >
              {value}
            </span>
          );
        }

        // -----------------------------------------------------
        // Apps
        // -----------------------------------------------------
        else if (match[4]) {
          const appName = match[4];

          const appKey = appName.toLowerCase();

          const route = APP_ROUTES[appKey] || '/apps';

          if (profileFromStorage?.permission !== 'Manager') {
            nodes.push(
              <span key={`app-${match.index}`} style={ls} onClick={() => history.push(handleCompanyUrl(route))}>
                {appName}
              </span>
            );
          } else {
            nodes.push(<span key={`app-${match.index}`}>{appName}</span>);
          }
        }

        // -----------------------------------------------------
        // Email
        // -----------------------------------------------------
        else if (match[5]) {
          const email = match[5];

          /*
           * Check whether email is inside parentheses.
           *
           * Example:
           *
           * (distinctio@easyidentity.co)
           *
           * This email MUST remain normal.
           */
          const textBeforeEmail = mainPart.slice(0, match.index);

          const lastOpenParen = textBeforeEmail.lastIndexOf('(');

          const lastCloseParen = textBeforeEmail.lastIndexOf(')');

          const isInsideParentheses = lastOpenParen > lastCloseParen;

          /*
           * Also check whether this email is the
           * first End-User email.
           *
           * We only want:
           *
           * End-User distinctio@easyidentity.co
           *
           * to be blue.
           */
          const textBeforeTrimmed = textBeforeEmail.trimEnd();

          const isEndUserEmail = /End-User\s*$/i.test(textBeforeTrimmed);

          if (!isInsideParentheses && isEndUserEmail) {
            nodes.push(
              <span
                key={`email-${match.index}`}
                style={ls}
                onClick={() => {
                  if (userId) {
                    getClickedUser.mutate(userId);
                  }
                }}
              >
                {email}
              </span>
            );
          } else {
            // Normal email
            nodes.push(<span key={`email-normal-${match.index}`}>{email}</span>);
          }
        }

        lastIndex = tokenRegex.lastIndex;
      }

      // -------------------------------------------------------
      // Remaining normal text
      // -------------------------------------------------------

      if (lastIndex < mainPart.length) {
        nodes.push(<span key={`remaining-${lastIndex}`}>{mainPart.slice(lastIndex)}</span>);
      }

      if (actorPart) {
        nodes.push(<span key='by'> {' by '} </span>);

        // First "." ke pehle tak BLUE
        // "." aur uske baad ka sab NORMAL
        const dotIndex = actorPart.indexOf('.');

        const actorName = dotIndex !== -1 ? actorPart.slice(0, dotIndex) : actorPart;

        const actorSuffix = dotIndex !== -1 ? actorPart.slice(dotIndex) : '';

        if (profileFromStorage?.permission !== 'Manager') {
          nodes.push(
            <span key='actor'>
              {/* Tests client -> BLUE */}
              <span
                style={ls}
                onClick={() => {
                  if (actorId) {
                    getClickedAdmin.mutate(actorId);
                  }
                }}
              >
                {actorName}
              </span>

              {/* . -> NORMAL */}
              <span>{actorSuffix}</span>
            </span>
          );
        } else {
          nodes.push(<span key='actor'>{actorPart}</span>);
        }
      }

      return <>{nodes}</>;
    }

    // =========================================================
    // 3. GROUP / DEPARTMENT COLON CASE
    //
    // Removed from group: GroupName
    // Removed as manager of department: DepartmentName
    // =========================================================

    const groupDeptColonMatch = !/\(/.test(mainPart) && mainPart.match(/^(.*?(?:group|department):\s*)(.+)$/);

    if (groupDeptColonMatch) {
      const [, prefix, entity] = groupDeptColonMatch;

      const entityId =
        event.group_id ||
        event.department_id ||
        event.details?.group_id ||
        event.details?.department_id ||
        event.user_id;

      let head: 'groups' | 'departments' | 'user';

      if (event.group_id || event.details?.group_id) {
        head = 'groups';
      } else if (event.department_id || event.details?.department_id) {
        head = 'departments';
      } else {
        head = 'user';
      }

      nodes.push(<span key='prefix'>{prefix}</span>);

      nodes.push(
        <span
          key='entity'
          style={ls}
          onClick={() => {
            if (entityId) {
              getClicked.mutate({
                id: entityId,
                type: head,
              });
            }
          }}
        >
          {entity.trim()}
        </span>
      );

      if (actorPart) {
        nodes.push(<span key='by'>{' by '}</span>);

        // "." se pehle ka part blue hoga
        const dotIndex = actorPart.indexOf('.');
        const actorName = dotIndex !== -1 ? actorPart.slice(0, dotIndex) : actorPart;
        const actorSuffix = dotIndex !== -1 ? actorPart.slice(dotIndex) : '';

        if (profileFromStorage?.permission !== 'Manager') {
          const actorId = event.actor_id || event.created_by_id || event.admin_id || event.updated_by;

          nodes.push(
            <span key='actor'>
              <span
                style={ls}
                onClick={() => {
                  if (actorId) {
                    getClickedAdmin.mutate(actorId);
                  }
                }}
              >
                {actorName}
              </span>

              <span>{actorSuffix}</span>
            </span>
          );
        } else {
          nodes.push(<span key='actor'>{actorPart}</span>);
        }
      }
      return <>{nodes}</>;
    }

    // =========================================================
    // 4. FROM / TO GROUP / DEPARTMENT
    //
    // Action (PersonName) from group: GroupName
    // =========================================================

    const fromGroupMatch = mainPart.match(/^(.*?)\(([^)]*)\)(\s+(?:from|to)\s+(?:group|department):\s*)(.+)$/);

    if (fromGroupMatch) {
      const [, before, personName, middle, groupName] = fromGroupMatch;

      const groupId =
        event.group_id ||
        event.department_id ||
        event.details?.group_id ||
        event.details?.department_id ||
        event.user_id;

      const personId =
        event.user_id ||
        event.manager?.user_id ||
        event.user?.ldap_dn ||
        (Array.isArray(event.managers) && event.managers.length === 1 ? event.managers[0]?.user_id : null) ||
        (Array.isArray(event.users) && event.users.length === 1 ? event.users[0]?.user_id : null) ||
        (Array.isArray(event.user_uids) && event.user_uids.length === 1 ? event.user_uids[0] : null);

      nodes.push(<span key='before'>{before}</span>);

      if (personName.trim()) {
        nodes.push(<span key='open'>(</span>);

        nodes.push(
          <span
            key='person'
            style={ls}
            onClick={() => {
              if (personId) {
                getClickedUser.mutate(personId);
              }
            }}
          >
            {personName}
          </span>
        );

        nodes.push(<span key='close'>)</span>);
      } else {
        nodes.push(<span key='emptyparens'>()</span>);
      }

      nodes.push(<span key='middle'>{middle}</span>);

      nodes.push(
        <span
          key='group'
          style={ls}
          onClick={() => {
            if (groupId) {
              const type =
                event.group_id || event.details?.group_id
                  ? 'groups'
                  : event.department_id || event.details?.department_id
                  ? 'departments'
                  : 'user';

              getClicked.mutate({
                id: groupId,
                type,
              });
            }
          }}
        >
          {groupName.trim()}
        </span>
      );

      // Actor

      if (actorPart) {
        nodes.push(<span key='by'>{' by '}</span>);

        // "." se pehle ka part blue hoga
        const dotIndex = actorPart.indexOf('.');
        const actorName = dotIndex !== -1 ? actorPart.slice(0, dotIndex) : actorPart;
        const actorSuffix = dotIndex !== -1 ? actorPart.slice(dotIndex) : '';

        if (profileFromStorage?.permission !== 'Manager') {
          const actorId = event.actor_id || event.created_by_id || event.admin_id || event.updated_by;

          nodes.push(
            <span key='actor'>
              <span
                style={ls}
                onClick={() => {
                  if (actorId) {
                    getClickedAdmin.mutate(actorId);
                  }
                }}
              >
                {actorName}
              </span>

              <span>{actorSuffix}</span>
            </span>
          );
        } else {
          nodes.push(<span key='actor'>{actorPart}</span>);
        }
      }

      return <>{nodes}</>;
    }

    // =========================================================
    // 5. GENERIC ENTITY PARSING
    // =========================================================

    const forIdx = mainPart.indexOf(' for ');

    const userIdx = mainPart.indexOf('(');

    const colonEndIdx = mainPart.lastIndexOf('): ');

    const idIdx = mainPart.indexOf('ID: ');

    /*
     * IMPORTANT:
     *
     * Don't use mainPart.indexOf("Name: ")
     * because that also matches:
     *
     * First Name:
     * Last Name:
     *
     * We only want standalone "Name: ".
     */

    const standaloneNameRegex = /(?:^|[\s,\]])Name:\s*/i;

    const standaloneNameMatch = mainPart.match(standaloneNameRegex);

    const nameIdx = standaloneNameMatch ? mainPart.indexOf('Name: ', standaloneNameMatch.index) : -1;

    let prefixPart = mainPart;

    let entityName: string | null = null;

    let entitySep = '';

    let entitySuffix = '';

    // =========================================================
    // Standalone Name:
    // =========================================================

    if (
      nameIdx !== -1 &&
      (idIdx === -1 || nameIdx < idIdx) &&
      (forIdx === -1 || nameIdx < forIdx) &&
      (userIdx === -1 || nameIdx < userIdx)
    ) {
      prefixPart = mainPart.slice(0, nameIdx);

      const rawEntityName = mainPart.slice(nameIdx + 'Name: '.length);

      const stopIdx = rawEntityName.search(/,|\]/);

      if (stopIdx !== -1) {
        entityName = rawEntityName.slice(0, stopIdx).trim();

        entitySuffix = rawEntityName.slice(stopIdx);
      } else {
        entityName = rawEntityName.trim();

        entitySuffix = '';
      }

      entitySep = 'Name: ';
    }

    // =========================================================
    // ID:
    // =========================================================
    else if (idIdx !== -1 && (forIdx === -1 || idIdx < forIdx) && (userIdx === -1 || idIdx < userIdx)) {
      prefixPart = mainPart.slice(0, idIdx);

      const rawValue = mainPart.slice(idIdx + 'ID: '.length);

      const stopIdx = rawValue.search(/,|\]/);

      if (stopIdx !== -1) {
        entityName = rawValue.slice(0, stopIdx).trim();

        entitySuffix = rawValue.slice(stopIdx);
      } else {
        entityName = rawValue.trim();

        entitySuffix = '';
      }

      entitySep = 'ID: ';
    }

    // =========================================================
    // " for "
    // =========================================================
    else if (forIdx !== -1) {
      prefixPart = mainPart.slice(0, forIdx);

      const rawValue = mainPart.slice(forIdx + ' for '.length);

      entityName = rawValue.trim();

      entitySep = ' for ';
    }

    // =========================================================
    // "(" entity
    // =========================================================
    else if (userIdx !== -1) {
      prefixPart = mainPart.slice(0, userIdx);

      const rawValue = mainPart.slice(userIdx + 1);

      const closeIdx = rawValue.indexOf(')');

      if (closeIdx !== -1) {
        entityName = rawValue.slice(0, closeIdx).trim();

        entitySuffix = rawValue.slice(closeIdx);
      } else {
        entityName = rawValue.trim();

        entitySuffix = '';
      }

      entitySep = ' (';
    }

    // =========================================================
    // "): "
    // =========================================================
    else if (colonEndIdx !== -1) {
      prefixPart = mainPart.slice(0, colonEndIdx + 2);

      entityName = mainPart.slice(colonEndIdx + 3).trim();

      entitySep = '';
    }

    // =========================================================
    // 6. Render prefix
    //
    // Apps are blue/clickable.
    // =========================================================

    if (profileFromStorage?.permission !== 'Manager') {
      const appRegex = /\b(Google|Zoom|Microsoft|Github|GitHub)\b/gi;

      let match: RegExpExecArray | null;

      let lastIdx = 0;

      while ((match = appRegex.exec(prefixPart)) !== null) {
        if (match.index > lastIdx) {
          nodes.push(<span key={`p-${lastIdx}`}>{prefixPart.slice(lastIdx, match.index)}</span>);
        }

        const appName = match[0];

        const appKey = appName.toLowerCase();

        const route = APP_ROUTES[appKey] || '/apps';

        nodes.push(
          <span key={`app-${match.index}`} style={ls} onClick={() => history.push(handleCompanyUrl(route))}>
            {appName}
          </span>
        );

        lastIdx = match.index + appName.length;
      }

      if (lastIdx < prefixPart.length) {
        nodes.push(<span key={`p-${lastIdx}`}>{prefixPart.slice(lastIdx)}</span>);
      }
    } else {
      nodes.push(<span key='prefix'>{prefixPart}</span>);
    }

    // =========================================================
    // 7. Render entity
    // =========================================================

    if (entityName) {
      nodes.push(<span key='sep'>{entitySep}</span>);

      const entityId = event.group_id || event.department_id || event.user_id;

      nodes.push(
        <span
          key='entity'
          style={ls}
          onClick={() => {
            if (entityId) {
              getClickedUser.mutate(entityId);
            }
          }}
        >
          {entityName}
        </span>
      );

      /*
       * IMPORTANT:
       *
       * comma / ] / remaining text
       * must remain visible and NORMAL.
       */
      if (entitySuffix) {
        nodes.push(<span key='entity-suffix'>{entitySuffix}</span>);
      }
    }

    if (actorPart) {
      nodes.push(<span key='by'>{' by '}</span>);

      // "." se pehle ka part blue hoga
      const dotIndex = actorPart.indexOf('.');
      const actorName = dotIndex !== -1 ? actorPart.slice(0, dotIndex) : actorPart;
      const actorSuffix = dotIndex !== -1 ? actorPart.slice(dotIndex) : '';

      if (profileFromStorage?.permission !== 'Manager') {
        const actorId = event.actor_id || event.created_by_id || event.admin_id || event.updated_by;

        nodes.push(
          <span key='actor'>
            <span
              style={ls}
              onClick={() => {
                if (actorId) {
                  getClickedAdmin.mutate(actorId);
                }
              }}
            >
              {actorName}
            </span>

            <span>{actorSuffix}</span>
          </span>
        );
      } else {
        nodes.push(<span key='actor'>{actorPart}</span>);
      }
    }
    return <>{nodes}</>;
  };

  useAppPreloader(loading);
  return (
    <Paper className={classes.paper}>
      <>
        {/* Toolbar: Filter + Export */}
        <div className={classes.toolbar}>
          <TextField
            size='small'
            placeholder='Search timeline...'
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search fontSize='small' />
                </InputAdornment>
              ),
            }}
            variant='outlined'
          />
          <Tooltip title='Export as Excel'>
            <IconButton size='small' onClick={handleExportExcel} disabled={filteredEvents.length === 0}>
              <GetApp />
            </IconButton>
          </Tooltip>
        </div>

        {filteredEvents.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant='body2' color='textSecondary'>
              No timeline events found
            </Typography>
          </div>
        ) : (
          <>
            <List className={classes.list}>
              {paginatedEvents.map((event: any, index: any) => (
                <ListItem key={event.id || index} className={classes.listItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.avatar}>{getIcon(event.type || 'user')}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <div className={classes.primaryText}>
                        <Typography
                          component='span'
                          style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                          onClick={() =>
                            getClickedUser.mutate(
                              events.user_id || events.group_id || events.department_id || events.entity_id
                            )
                          }
                        >
                          {displayName || 'Unknown User'}
                        </Typography>
                        <Chip
                          label={event.type || 'activity'}
                          size='small'
                          color={getTypeColor(event.type || 'user') as any}
                          className={classes.typeChip}
                        />
                      </div>
                    }
                    secondary={
                      <div className={classes.secondaryText}>
                        {displayEmail && (
                          <Typography variant='body2' color='textSecondary'>
                            Email:{' '}
                            <span
                              style={{
                                color: '#1976d2',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                              onClick={() =>
                                getClickedUser.mutate(
                                  events.user_id || events.group_id || events.department_id || events.entity_id
                                )
                              }
                            >
                              {displayEmail}
                            </span>
                          </Typography>
                        )}

                        <div className={classes.secondaryText}>
                          {event.raw_description && (
                            <Typography variant='body2' className={classes.description}>
                              {renderDescription(event)}
                            </Typography>
                          )}
                          <Typography variant='caption' color='textSecondary'>
                            {event.created_at
                              ? new Date(event.created_at).toLocaleString()
                              : event.createdAt
                              ? new Date(event.createdAt).toLocaleString()
                              : event.createtimestamp
                              ? new Date(event.createtimestamp).toLocaleString()
                              : 'Unknown date'}
                          </Typography>
                        </div>
                      </div>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <TablePagination
              component='div'
              count={filteredEvents.length}
              page={page}
              onChangePage={(_: any, newPage: number) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e: any) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </>
    </Paper>
  );
};

export default Timeline;
